import { useTranslation } from "react-i18next";
import { UserPlus, X } from "lucide-react";

import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Label from "../../../../shared/ui/form/Label";
import { getAvatarColor } from "../../../../shared/utils/avatar";
import { MAX_GROUP_SIZE, MIN_GROUP_SIZE } from "../../domain/GroupPatient";

export interface PickedMember {
    id: string;
    name: string;
}

interface Props {
    members: PickedMember[];
    /** Patients who may join: the caller's, not in the group, matching ownership. */
    candidates: { value: string; label: string }[];
    onAdd: (patientId: string) => void;
    onRemove: (member: PickedMember) => void;
    loading?: boolean;
    disabled?: boolean;
    /** Render each member's name as a link, on a group that already exists. */
    renderName?: (member: PickedMember) => React.ReactNode;
    /**
     * Refuse to take anybody out at three. On for a group that exists; off
     * while one is being put together, where the choice is still a draft.
     */
    keepMinimum?: boolean;
}

/**
 * Who is in the group: the people chosen so far, and a search to add one more.
 *
 * The bounds are shown rather than discovered: the picker says how many a
 * group takes, stops offering people once it is full, and does not let the
 * last three be taken out one by one.
 */
export default function GroupMemberPicker({
    members,
    candidates,
    onAdd,
    onRemove,
    loading = false,
    disabled = false,
    renderName,
    keepMinimum = true,
}: Props) {
    const { t } = useTranslation();
    const full = members.length >= MAX_GROUP_SIZE;
    const atMinimum = keepMinimum && members.length <= MIN_GROUP_SIZE;

    return (
        <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
                <Label className="mb-0">{t("patient.group.members")}</Label>
                <span className="text-xs tabular-nums text-content-tertiary">
                    {t("patient.group.membersCount", { count: members.length })} ·{" "}
                    {t("patient.group.sizeHint", { min: MIN_GROUP_SIZE, max: MAX_GROUP_SIZE })}
                </span>
            </div>

            {members.length > 0 && (
                <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
                    {members.map((member) => (
                        <li key={member.id} className="flex items-center gap-3 px-3 py-2">
                            <span
                                aria-hidden="true"
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold uppercase text-white ${getAvatarColor(member.id)}`}
                            >
                                {member.name.charAt(0)}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                                {renderName ? renderName(member) : member.name}
                            </span>
                            <button
                                type="button"
                                onClick={() => onRemove(member)}
                                disabled={disabled || atMinimum}
                                title={
                                    atMinimum
                                        ? t("patient.group.atMinimum", { min: MIN_GROUP_SIZE })
                                        : undefined
                                }
                                aria-label={t("patient.group.removeMember", { name: member.name })}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500 dark:text-gray-400 dark:hover:bg-red-500/10"
                            >
                                <X className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {full ? (
                <p className="text-xs text-content-tertiary">
                    {t("patient.group.full", { max: MAX_GROUP_SIZE })}
                </p>
            ) : (
                <FilterSelect
                    options={candidates}
                    value=""
                    onChange={(value) => value && onAdd(value as string)}
                    placeholder={loading ? t("common.loading") : t("patient.group.addMemberPlaceholder")}
                    searchPlaceholder={t("patient.searchPlaceholder")}
                    icon={<UserPlus className="h-4 w-4" aria-hidden="true" />}
                    label={t("patient.group.addMember")}
                    disabled={disabled}
                />
            )}
        </div>
    );
}
